// src/hooks/useClients.ts - Version multi-tenant

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Client } from '../types/entities';
import { trackUsage } from '../utils/usageTracking';
import { useAuth } from '../contexts/AuthContext';  // NOUVEAU
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { usePermissions } from './usePermissions';
import {
  applyOptionalClientTextFields,
  mapFirestoreDocToClient,
} from '../utils/clientMapper';
import {
  findDuplicateClientByPhone,
  resolvePersistableClientPhone,
} from '../utils/clientPhoneUniqueness';
import {
  runSuperAdminBulkImportClients,
  type ClientBulkImportProgressEvent,
} from '../utils/clientBulkImport';
import type { ClientImportPersistRow } from '../features/super-admin/utils/clientImportExport';
import { AppError } from '../utils/i18n/AppError';

export type AddClientInput = Omit<Client, 'id' | 'businessId'> & {
  /** Si true : réutilise la fiche existante au même numéro (ramassage → caisse). */
  reuseExistingByPhone?: boolean;
};

async function listBusinessClientsForPhoneCheck(
  businessId: string
): Promise<Array<{ id: string; phone?: string }>> {
  const q = query(
    collection(db, COLLECTIONS.clients),
    where('businessId', '==', businessId)
  );
  const snapshot = await getDocs(q).catch((error) => {
    handleFirestoreError(error, 'useClients - phone uniqueness query');
    throw error;
  });
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data() as Record<string, unknown>;
    return {
      id: docSnap.id,
      phone: typeof data.phone === 'string' ? data.phone : undefined,
    };
  });
}

// ===== HOOKS POUR LES CLIENTS =====
export const useClients = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['clients', businessId],
    queryFn: async () => {
      if (!businessId) {
        console.warn('❌ Aucun businessId disponible');
        return [];
      }

      const q = query(
        collection(db, COLLECTIONS.clients), 
        where('businessId', '==', businessId),
        orderBy('name')
      );
      const snapshot = await getDocs(q).catch(error => {
        handleFirestoreError(error, 'useClients - Main query');
        throw error;
      });
      
      return snapshot.docs.map((docSnap) =>
        mapFirestoreDocToClient(docSnap.id, docSnap.data() as Record<string, unknown>)
      );
    },
    // Activer si l'utilisateur peut voir les clients OU voir les opportunités (pour rattacher un client dans le formulaire opportunité)
    enabled:
      !!businessId &&
      (!!(permissions?.canViewClients ?? false) || !!(permissions?.canViewSalesPipeline ?? false)),
  });
};

export const useAddClient = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (client: AddClientInput) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }

      const phone = resolvePersistableClientPhone(client.phone);
      if (!phone) {
        throw new AppError('clients.phoneRequired');
      }

      const existing = findDuplicateClientByPhone(
        await listBusinessClientsForPhoneCheck(businessId),
        phone
      );
      if (existing) {
        if (client.reuseExistingByPhone) {
          return existing.id;
        }
        throw new AppError('clients.phoneAlreadyExists');
      }

      const cleanedClient: Record<string, unknown> = {
        name: client.name.trim(),
        businessId,
        phone,
        loyaltyBalance: client.loyaltyBalance || 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      applyOptionalClientTextFields(cleanedClient, { ...client, phone });

      if (client.birthDate) {
        cleanedClient.birthDate = Timestamp.fromDate(client.birthDate);
      }

      if (client.gender) {
        cleanedClient.gender = client.gender;
      }

      if (client.emergencyContact) {
        cleanedClient.emergencyContact = client.emergencyContact;
      }

      if (client.preferences) {
        cleanedClient.preferences = client.preferences;
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.clients), cleanedClient);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', businessId] });
      trackUsage('client_created');
    },
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;  // NOUVEAU
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Client> & { id: string }) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }

      if (data.hasOwnProperty('phone')) {
        const phone = resolvePersistableClientPhone(data.phone);
        if (!phone) {
          throw new AppError('clients.phoneRequired');
        }
        const duplicate = findDuplicateClientByPhone(
          await listBusinessClientsForPhoneCheck(businessId),
          phone,
          id
        );
        if (duplicate) {
          throw new AppError('clients.phoneAlreadyExists');
        }
        data = { ...data, phone };
      }

      // Préparer les données de mise à jour client
      const updateData: any = {};
      
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'birthDate' && key !== 'loyaltyBalance' && key !== 'businessId' && key !== 'id') {
          if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (trimmedValue === '') {
              updateData[key] = deleteField();
            } else {
              updateData[key] = trimmedValue;
            }
          } else if (value !== undefined) {
            updateData[key] = value;
          }
        }
      });

      // Toujours mettre à jour updatedAt
      updateData.updatedAt = Timestamp.now();
      
      if (data.birthDate) {
        updateData.birthDate = Timestamp.fromDate(data.birthDate);
      } else if (data.hasOwnProperty('birthDate') && data.birthDate === null) {
        updateData.birthDate = deleteField();
      }
      
      const clientRef = doc(db, COLLECTIONS.clients, id);
      await updateDoc(clientRef, updateData);

      // Synchroniser les promotions actives si nom ou téléphone modifié
      if (data.name || data.phone) {
        try {
          console.log('🔄 Synchronisation des promotions pour client:', id);
          
          // MODIFIÉ : Filtrer les promotions par institut aussi
          const promotionsQuery = query(
            collection(db, COLLECTIONS.promotions),
            where('businessId', '==', businessId),  // NOUVEAU
            where('clientId', '==', id),
            where('used', '==', false)
          );
          
          const promotionsSnap = await getDocs(promotionsQuery).catch(error => {
            handleFirestoreError(error, 'useClients - Promotions query');
            throw error;
          });
          console.log(`📧 ${promotionsSnap.size} promotions actives trouvées`);
          
          const updatePromises = promotionsSnap.docs.map(async (promotionDoc) => {
            const promotionUpdateData: any = {};
            
            if (data.name && typeof data.name === 'string' && data.name.trim()) {
              promotionUpdateData.clientName = data.name.trim();
            }
            
            if (data.phone && typeof data.phone === 'string' && data.phone.trim()) {
              promotionUpdateData.clientPhone = data.phone.trim();
            }
            
            if (Object.keys(promotionUpdateData).length > 0) {
              console.log(`📝 Mise à jour promotion ${promotionDoc.id}:`, promotionUpdateData);
              await updateDoc(promotionDoc.ref, promotionUpdateData);
            }
          });
          
          await Promise.all(updatePromises);
          console.log('✅ Synchronisation des promotions terminée');
          
        } catch (error) {
          console.warn('⚠️ Erreur lors de la synchronisation des promotions:', error);
        }
      }

      return { id, ...data };
    },
    onSuccess: () => {
      // MODIFIÉ : Invalider avec businessId
      queryClient.invalidateQueries({ queryKey: ['clients', businessId] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'promotions'] });
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;  // NOUVEAU
  
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, COLLECTIONS.clients, id));
    },
    onSuccess: () => {
      // MODIFIÉ : Invalider avec businessId
      queryClient.invalidateQueries({ queryKey: ['clients', businessId] });
    },
  });
};

// ✅ NOUVEAU : Hook pour récupérer un client spécifique
export const useClient = (clientId: string | undefined) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['clients', businessId, clientId],
    queryFn: async () => {
      if (!clientId || !businessId) {
        return null;
      }

      const docRef = doc(db, COLLECTIONS.clients, clientId);
      const docSnap = await getDoc(docRef).catch((error: any) => {
        handleFirestoreError(error, 'useClient - Get document');
        throw error;
      });
      
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      
      // Vérifier que le client appartient au business
      if (data.businessId !== businessId) {
        console.warn('⚠️ Client ne correspond pas au business actuel');
        return null;
      }
      
      return mapFirestoreDocToClient(docSnap.id, data as Record<string, unknown>);
    },
    enabled: !!clientId && !!businessId && (permissions?.canViewClients ?? false),
  });
};

export const useClientsForBusinessId = (
  businessId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['clients', 'forBusiness', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const q = query(
        collection(db, COLLECTIONS.clients),
        where('businessId', '==', businessId),
        orderBy('name')
      );
      const snapshot = await getDocs(q).catch((error) => {
        handleFirestoreError(error, 'useClientsForBusinessId');
        throw error;
      });

      return snapshot.docs.map((docSnap) =>
        mapFirestoreDocToClient(docSnap.id, docSnap.data() as Record<string, unknown>)
      );
    },
    enabled: !!businessId && options?.enabled !== false,
  });
};

export const useSuperAdminBulkImportClients = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM, userProfile } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      businessId: string;
      rows: ClientImportPersistRow[];
      onProgress?: (e: ClientBulkImportProgressEvent) => void;
    }) => {
      const { businessId, rows, onProgress } = params;
      return runSuperAdminBulkImportClients({
        businessId,
        rows,
        auth: {
          isSuperAdmin,
          isCSM,
          userBusinessIds: userProfile?.businessIds,
        },
        onProgress,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'forBusiness', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['clients', variables.businessId] });
    },
  });
};